import solarTerms from '../data/solar_terms.json';
import solarTermTable from '../data/solar_terms_utc.json';
import {
  bisectSolarLongitude,
  findSolarTermDateByYearScan,
  fromJulianDay,
  getSolarLongitude,
  isLongitudeBetween,
  toJulianDay
} from '../../utils/astro/solarTermsCore.js';

const DAY_MS = 24 * 60 * 60 * 1000;

const SOLAR_TERM_LONGITUDES = {
  'Xuan Phan': 0,
  'Thanh Minh': 15,
  'Coc Vu': 30,
  'Lap Ha': 45,
  'Tieu Man': 60,
  'Mang Chung': 75,
  'Ha Chi': 90,
  'Tieu Thu': 105,
  'Dai Thu': 120,
  'Lap Thu': 135,
  'Xu Thu': 150,
  'Bach Lo': 165,
  'Thu Phan': 180,
  'Han Lo': 195,
  'Suong Giang': 210,
  'Lap Dong': 225,
  'Tieu Tuyet': 240,
  'Dai Tuyet': 255,
  'Dong Chi': 270,
  'Tieu Han': 285,
  'Dai Han': 300,
  'Lap Xuan': 315,
  'Vu Thuy': 330,
  'Kinh Trập': 345
};

const SOLAR_TERM_CACHE = new Map();
const SOLAR_TERM_DATE_CACHE = new Map();
const CACHE_RANGE = 5;
const CACHE_LIMIT = 200;

const TABLE_YEAR_MIN = 1900;
const TABLE_YEAR_MAX = 2100;

const SOLAR_TERM_ESTIMATES = new Map(
  solarTerms.map((term) => [term.name, { month: term.month, day: term.day }])
);

const getSolarTermDateFromTable = (year, termName) => {
  if (year < TABLE_YEAR_MIN || year > TABLE_YEAR_MAX) return null;
  const byYear = solarTermTable?.[year];
  const iso = byYear ? byYear[termName] : null;
  return iso ? new Date(iso) : null;
};

const findSolarTermDate = (year, termName, targetLongitude) => {
  const tableDate = getSolarTermDateFromTable(year, termName);
  if (tableDate) return tableDate;

  const estimate = SOLAR_TERM_ESTIMATES.get(termName);
  if (!estimate) {
    return findSolarTermDateByYearScan(year, targetLongitude);
  }

  let start = new Date(Date.UTC(year, estimate.month - 1, estimate.day, 0, 0, 0));
  let end = new Date(start.getTime() + 2 * DAY_MS);
  start = new Date(start.getTime() - 2 * DAY_MS);

  let startLongitude = getSolarLongitude(toJulianDay(start));
  let endLongitude = getSolarLongitude(toJulianDay(end));

  for (let i = 0; i < 8 && !isLongitudeBetween(startLongitude, endLongitude, targetLongitude); i += 1) {
    start = new Date(start.getTime() - DAY_MS);
    end = new Date(end.getTime() + DAY_MS);
    startLongitude = getSolarLongitude(toJulianDay(start));
    endLongitude = getSolarLongitude(toJulianDay(end));
  }

  if (!isLongitudeBetween(startLongitude, endLongitude, targetLongitude)) {
    return findSolarTermDateByYearScan(year, targetLongitude);
  }

  const startJd = toJulianDay(start);
  const endJd = toJulianDay(end);
  const crossingJd = bisectSolarLongitude(startJd, endJd, targetLongitude, 50);
  return fromJulianDay(crossingJd);
};

export const getSolarTermDate = (year, termName) => {
  const longitude = SOLAR_TERM_LONGITUDES[termName];
  if (longitude === undefined) return null;
  const cacheKey = `${year}-${termName}`;
  if (SOLAR_TERM_DATE_CACHE.has(cacheKey)) {
    return SOLAR_TERM_DATE_CACHE.get(cacheKey);
  }

  const result = findSolarTermDate(year, termName, longitude);
  SOLAR_TERM_DATE_CACHE.set(cacheKey, result);

  if (SOLAR_TERM_DATE_CACHE.size < CACHE_LIMIT) {
    for (let offset = -CACHE_RANGE; offset <= CACHE_RANGE; offset += 1) {
      const prefetchYear = year + offset;
      const prefetchKey = `${prefetchYear}-${termName}`;
      if (!SOLAR_TERM_DATE_CACHE.has(prefetchKey)) {
        const prefetchResult = findSolarTermDate(prefetchYear, termName, longitude);
        SOLAR_TERM_DATE_CACHE.set(prefetchKey, prefetchResult);
      }
    }
  }

  return result;
};

export const listSolarTermsForYear = (year) => {
  if (SOLAR_TERM_CACHE.has(year)) {
    return SOLAR_TERM_CACHE.get(year);
  }

  const terms = solarTerms.map((term) => {
    const date = getSolarTermDate(year, term.name);
    return {
      ...term,
      date,
      month: date ? date.getUTCMonth() + 1 : term.month,
      day: date ? date.getUTCDate() : term.day
    };
  });

  SOLAR_TERM_CACHE.set(year, terms);
  return terms;
};
